import { resolveFeatureFlag } from '@hungryroot/feature-flags';
import {
  log,
  logExceptionOnce,
  logUnknownErrorResponse,
} from '@hungryroot/o11y';
import {
  getCustomerSelf,
  postApiV2CustomersCustomerIdResetPlan,
  StatusCode,
} from '@hungryroot/rest';
import { serverPersist } from '../data/server-persist';
import {
  clearQuizLocalStorage,
  DEFAULT_QUIZ_STORAGE_NAMESPACE,
  MINI_QUIZ_STORAGE_NAMESPACE,
} from '../defs/localStorage';
import {
  readLoginQuizPersistCandidate,
  type LoginQuizPersistCandidate,
} from './readLoginQuizPersistCandidate';

export const LOGIN_QUIZ_PERSIST_FLAG =
  'web.feature-flag.login-quiz-persist.enabled';

/**
 * Upper bound on how long a login redirect waits for the persist. Past it the
 * visitor is sent on regardless; the local quiz stays put for the next visit.
 */
const LOGIN_QUIZ_PERSIST_DEADLINE_MS = 10_000;

/** The slice of the customer this flow needs; `SignupResponse` and `Customer` both satisfy it. */
export interface LoginPersistCustomer {
  id: number;
  did_checkout: boolean;
}

interface Params {
  /**
   * The customer who just logged in, when the login response carried one
   * (SSO). Omit to have the flow fetch `/customers/self/` — only done after
   * the cheap local-storage and flag gates pass, so a login with nothing to
   * persist costs no extra request.
   */
  customer?: LoginPersistCustomer;
}

export type LoginQuizPersistOutcome =
  | 'no-candidate'
  | 'flag-off'
  | 'no-customer'
  | 'checked-out'
  | 'persisted'
  | 'failed'
  | 'timed-out';

async function fetchLoginCustomer(): Promise<LoginPersistCustomer | null> {
  const resp = await getCustomerSelf();
  if (resp.status !== StatusCode.SuccessOK) {
    logUnknownErrorResponse('Failed to load customer after login', {
      key: 'persistLocalQuizOnLogin.customerSelf',
      status: resp.status,
    });
    return null;
  }
  return resp.data;
}

/**
 * Plan reset (BE-8695). Required, not best-effort: `reset_plan` is what
 * records the `RESET_SURVEY` log that makes the funnel islands' later
 * `reset_survey` call a no-op. If it does not land, the just-persisted
 * answers sit next to a stale plan, and the first quiz screen the guest
 * mounts after login would reset the survey and wipe them. A `{ reset: false }`
 * 200 is still success — the backend has confirmed nothing needed resetting.
 */
async function resetPlan(customerId: number): Promise<void> {
  const resp = await postApiV2CustomersCustomerIdResetPlan({ customerId });
  if (resp.status !== StatusCode.SuccessOK) {
    throw new Error(
      `Plan reset after login persist was rejected (status ${resp.status})`
    );
  }
}

type PersistStage = 'persist' | 'reset-plan';

async function persistCandidate({
  candidate,
  customer,
}: {
  candidate: LoginQuizPersistCandidate;
  customer: LoginPersistCustomer;
}): Promise<{ failedStage: PersistStage; error: unknown } | null> {
  // No `originalQuiz`: this is a fresh submission, so every slice is written
  // from the local quiz — deselected answers land as nulls instead of being
  // skipped by the diff — and the quizzes row goes through the
  // conflict-tolerant create (an existing guest already has one).
  try {
    await serverPersist(customer.id, candidate.quiz);
  } catch (error) {
    return { failedStage: 'persist', error };
  }

  try {
    await resetPlan(customer.id);
  } catch (error) {
    return { failedStage: 'reset-plan', error };
  }

  // Only now is the account in a consistent state (answers + no stale plan),
  // so only now is the local copy safe to drop. Clear both funnels so neither
  // a stale full-quiz nor mini-quiz copy can resurface on a later visit.
  clearQuizLocalStorage(DEFAULT_QUIZ_STORAGE_NAMESPACE);
  clearQuizLocalStorage(MINI_QUIZ_STORAGE_NAMESPACE);
  return null;
}

async function run({ customer }: Params): Promise<LoginQuizPersistOutcome> {
  const candidate = readLoginQuizPersistCandidate();
  if (candidate === null) {
    return 'no-candidate';
  }

  const isEnabled = await resolveFeatureFlag(LOGIN_QUIZ_PERSIST_FLAG);
  if (isEnabled !== true) {
    return 'flag-off';
  }

  const resolvedCustomer = customer ?? (await fetchLoginCustomer());
  if (resolvedCustomer === null) {
    return 'no-customer';
  }
  if (resolvedCustomer.did_checkout) {
    return 'checked-out';
  }

  const failure = await persistCandidate({
    candidate,
    customer: resolvedCustomer,
  });
  if (failure !== null) {
    // Nothing is cleared on failure, so the quiz is still there to retry on
    // the next login. A `reset-plan` failure after a successful persist
    // re-submits the same answers next time, which is idempotent.
    logExceptionOnce(failure.error, {
      key: 'persistLocalQuizOnLogin.persist.failed',
      stage: failure.failedStage,
      namespace: candidate.namespace,
      customerId: resolvedCustomer.id,
    });
    return 'failed';
  }

  log('Persisted local quiz on login', {
    key: 'persistLocalQuizOnLogin.persisted',
    namespace: candidate.namespace,
    customerId: resolvedCustomer.id,
  });
  return 'persisted';
}

/**
 * Exp 260: when a pre-checkout guest logs in with a completed, unexpired
 * locally stored quiz (full quiz or mini quiz), treat it as a fresh quiz
 * submission — persist it to the account, reset the plan so the plan page
 * builds a new one, and clear the local copy — then let the caller redirect
 * as it would have anyway.
 *
 * Never throws and never takes longer than {@link LOGIN_QUIZ_PERSIST_DEADLINE_MS}:
 * login must complete whatever happens here. Callers `await` it before the
 * post-login redirect, since a full-page navigation would cancel in-flight
 * requests.
 *
 * Off unless `web.feature-flag.login-quiz-persist.enabled` is on.
 */
export async function persistLocalQuizOnLogin(
  params: Params = {}
): Promise<LoginQuizPersistOutcome> {
  let timeout: ReturnType<typeof setTimeout> | undefined;
  let outcome: LoginQuizPersistOutcome;
  const deadline = new Promise<LoginQuizPersistOutcome>((resolve) => {
    timeout = setTimeout(() => {
      logExceptionOnce('Login quiz persist exceeded its deadline', {
        key: 'persistLocalQuizOnLogin.timedOut',
      });
      resolve('timed-out');
    }, LOGIN_QUIZ_PERSIST_DEADLINE_MS);
  });

  try {
    outcome = await Promise.race([run(params), deadline]);
  } catch (error) {
    logExceptionOnce(error, { key: 'persistLocalQuizOnLogin.unexpected' });
    outcome = 'failed';
  } finally {
    clearTimeout(timeout);
  }

  log('Resolved local quiz persistence on login', {
    key: 'persistLocalQuizOnLogin.outcome',
    outcome,
  });
  return outcome;
}
